import type { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'

import type { JwtTokenService } from './jwtTokenService'
import type { ReissueSingleFlightScheduler } from './ReissueSingleFlightScheduler'

/**
 * withoutAuth 옵션을 추가한 AxiosRequestConfig
 * @property withoutAuth - true로 설정하면 JWT 토큰 삽입을 건너뜁니다
 */
export interface CustomRequestConfig<D = any> extends AxiosRequestConfig<D> {
  withoutAuth?: boolean
}

/**
 * Request Interceptor에 JWT 로직을 추가하는 핵심 wrapper
 *
 * @internal
 */
const wrapRequestWithJwtLogic = (
  tokenService: JwtTokenService,
  scheduler: ReissueSingleFlightScheduler,
  originalRequestHandler: (
    config: InternalAxiosRequestConfig,
  ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
) => {
  return async (config: InternalAxiosRequestConfig & CustomRequestConfig) => {
    // withoutAuth가 true인 경우 JWT 로직 건너뛰기
    if (config.withoutAuth === true) {
      return originalRequestHandler(config)
    }

    // Authorization 헤더 추가
    const accessToken = tokenService.getAccessToken()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    // 3. 원본 handler 실행
    return originalRequestHandler(config)
  }
}

/**
 * Request Interceptor Wrapper Factory
 *
 * tokenService와 scheduler를 미리 바인딩하여 재사용 가능한 wrapper를 생성합니다.
 *
 * @param tokenService - JwtTokenService 인스턴스
 * @param scheduler - ReissueSingleFlightScheduler 인스턴스
 * @returns originalRequestHandler를 받아 JWT 로직이 추가된 interceptor를 반환하는 함수
 *
 * @example
 * // 앱 초기화 시 한 번만 생성
 * const wrapRequestInterceptorWithJwt = createRequestInterceptorWrapper(
 *   jwtTokenService,
 *   reissueSingleFlightScheduler
 * )
 *
 * // 각 axios 인스턴스에서 재사용
 * maxios.interceptors.request.use(
 *   wrapRequestInterceptorWithJwt(async (config) => {
 *     // 앱별 로직
 *     return config
 *   })
 * )
 */
export const createRequestInterceptorWrapper = (
  tokenService: JwtTokenService,
  scheduler: ReissueSingleFlightScheduler,
) => {
  return (
    originalRequestHandler: (
      config: InternalAxiosRequestConfig,
    ) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
  ) => wrapRequestWithJwtLogic(tokenService, scheduler, originalRequestHandler)
}

/**
 * Error Handler에 JWT 401 처리 로직을 추가하는 핵심 wrapper
 *
 * @internal
 */
const wrapErrorWithJwtRetry = <T = unknown>(
  tokenService: JwtTokenService,
  scheduler: ReissueSingleFlightScheduler,
  originalErrorHandler: (error: unknown) => Promise<T>,
  retryWithNewToken: (
    originalRequest: InternalAxiosRequestConfig,
    newAccessToken: string,
  ) => Promise<T>,
  onExpired?: () => void | Promise<void>,
) => {
  return async (error: unknown) => {
    const axiosError = error as AxiosError
    const originalRequest = axiosError.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // 401 에러이고, 재시도하지 않은 요청인 경우
    if (axiosError.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        // 1. 토큰 재발급
        await scheduler.start(() => tokenService.reissueTokens())

        // 2. 새 토큰 가져오기
        const newAccessToken = tokenService.getAccessToken()
        if (!newAccessToken) {
          throw new Error('No access token after reissue')
        }

        // 3. 새 토큰으로 요청 재시도
        return retryWithNewToken(originalRequest, newAccessToken)
      } catch (reissueError) {
        // 재발급 실패 시 onExpired 콜백 실행 후 앱별 에러 처리
        await onExpired?.()
        return await originalErrorHandler(error)
      }
    }

    // 401이 아니거나 이미 재시도한 경우 원본 에러 핸들러로 처리
    return originalErrorHandler(error)
  }
}

/**
 * Response Error Handler Wrapper Factory
 *
 * tokenService와 scheduler를 미리 바인딩하여 재사용 가능한 wrapper를 생성합니다.
 *
 * @param tokenService - JwtTokenService 인스턴스
 * @param scheduler - ReissueSingleFlightScheduler 인스턴스
 * @param onExpired - reissue 실패(refresh token 만료) 시 실행할 콜백 (로그아웃, 리디렉션 등)
 * @returns originalErrorHandler와 retryWithNewToken을 받아 JWT 401 처리 로직이 추가된 error handler를 반환하는 함수
 *
 * @example
 * // 앱 초기화 시 한 번만 생성
 * const wrapErrorHandlerWithJwt = createErrorHandlerWrapper(
 *   jwtTokenService,
 *   reissueSingleFlightScheduler,
 *   () => {
 *     // refresh token도 만료된 경우 처리
 *     jwtTokenService.clearTokens()
 *     window.location.href = '/login'
 *   }
 * )
 *
 * // 각 axios 인스턴스에서 재사용
 * maxios.interceptors.response.use(
 *   (response) => response,
 *   wrapErrorHandlerWithJwt(
 *     async (error) => {
 *       // 앱별 에러 처리 로직
 *     },
 *     async (originalRequest, newAccessToken) => {
 *       originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
 *       return maxios(originalRequest)
 *     }
 *   )
 * )
 */
export const createErrorHandlerWrapper = <T = unknown>(
  tokenService: JwtTokenService,
  scheduler: ReissueSingleFlightScheduler,
  onExpired?: () => void | Promise<void>,
) => {
  return (
    originalErrorHandler: (error: unknown) => Promise<T>,
    retryWithNewToken: (
      originalRequest: InternalAxiosRequestConfig,
      newAccessToken: string,
    ) => Promise<T>,
  ) =>
    wrapErrorWithJwtRetry(
      tokenService,
      scheduler,
      originalErrorHandler,
      retryWithNewToken,
      onExpired,
    )
}
